/**
 * Scaffold API Client Tool
 *
 * Generates TypeScript API clients that integrate with SmartStack's NavRoute system.
 * Creates type-safe API services with automatic route resolution from NavRoute paths.
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';
import path from 'path';

import { Config, ScaffoldApiClientInputSchema, ScaffoldApiClientInput, ScaffoldResult } from '../types/index.js';
import { logger } from '../lib/logger.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { ensureDirectory, writeText } from '../utils/fs.js';

export const scaffoldApiClientTool: Tool = {
  name: 'scaffold_api_client',
  description: `Generate TypeScript API client with NavRoute integration.

Creates:
- Type-safe API service with CRUD methods
- TypeScript interfaces for request/response
- React Query hook (optional)
- Integration with navRoutes.generated.ts registry

Example:
  scaffold_api_client navRoute="platform.administration.users" name="User"

The generated client automatically resolves the API path from the NavRoute registry,
ensuring frontend routes stay synchronized with backend NavRoute attributes.`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Path to SmartStack project root (defaults to configured project path)',
      },
      navRoute: {
        type: 'string',
        description: 'NavRoute path (e.g., "platform.administration.users")',
      },
      name: {
        type: 'string',
        description: 'Entity name in PascalCase (e.g., "User", "Order")',
      },
      methods: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['getAll', 'getById', 'create', 'update', 'delete', 'search', 'export'],
        },
        default: ['getAll', 'getById', 'create', 'update', 'delete'],
        description: 'API methods to generate',
      },
      options: {
        type: 'object',
        properties: {
          outputPath: { type: 'string', description: 'Custom output path' },
          includeTypes: { type: 'boolean', default: true, description: 'Generate TypeScript types' },
          includeHook: { type: 'boolean', default: true, description: 'Generate React Query hook' },
          dryRun: { type: 'boolean', default: false, description: 'Preview without writing' },
        },
      },
    },
    required: ['navRoute', 'name'],
  },
};

export async function handleScaffoldApiClient(
  args: Record<string, unknown>,
  config: Config
): Promise<string> {
  const input = ScaffoldApiClientInputSchema.parse(args);
  logger.info('Scaffolding API client', { navRoute: input.navRoute, name: input.name });

  const result = await scaffoldApiClient(input, config);
  return formatResult(result, input);
}

async function scaffoldApiClient(
  input: ScaffoldApiClientInput,
  config: Config
): Promise<ScaffoldResult> {
  const result: ScaffoldResult = {
    success: true,
    files: [],
    instructions: [],
  };

  const { navRoute, name, methods, options } = input;
  const dryRun = options?.dryRun ?? false;
  const includeTypes = options?.includeTypes ?? true;
  const includeHook = options?.includeHook ?? true;

  const nameLower = name.charAt(0).toLowerCase() + name.slice(1);
  const apiPath = navRouteToApiPath(navRoute);

  // Use explicit path if provided, otherwise fall back to config
  const projectRoot = input.path || config.smartstack.projectPath;
  const structure = await findSmartStackStructure(projectRoot);
  // Fallback to 'web' folder (not hardcoded to smartstack-web)
  const webPath = structure.web || path.join(projectRoot, 'web');
  const servicesPath = options?.outputPath || path.join(webPath, 'src', 'services', 'api');
  const hooksPath = path.join(webPath, 'src', 'hooks');
  const typesPath = path.join(webPath, 'src', 'types');

  // Generate API client
  const apiClientContent = generateApiClient(name, nameLower, navRoute, apiPath, methods);
  const apiClientFile = path.join(servicesPath, `${nameLower}.ts`);

  if (!dryRun) {
    await ensureDirectory(servicesPath);
    await writeText(apiClientFile, apiClientContent);
  }
  result.files.push({ path: apiClientFile, content: apiClientContent, type: 'created' });

  // Generate TypeScript types
  if (includeTypes) {
    const typesContent = generateTypes(name);
    const typesFile = path.join(typesPath, `${nameLower}.ts`);

    if (!dryRun) {
      await ensureDirectory(typesPath);
      await writeText(typesFile, typesContent);
    }
    result.files.push({ path: typesFile, content: typesContent, type: 'created' });
  }

  // Generate React Query hook
  if (includeHook) {
    const hookContent = generateHook(name, nameLower, methods);
    const hookFile = path.join(hooksPath, `use${name}.ts`);

    if (!dryRun) {
      await ensureDirectory(hooksPath);
      await writeText(hookFile, hookContent);
    }
    result.files.push({ path: hookFile, content: hookContent, type: 'created' });
  }

  result.instructions.push(`Import the API client: import { ${nameLower}Api } from './services/api/${nameLower}';`);
  if (includeHook) {
    result.instructions.push(`Import the hook: import { use${name}, use${name}List } from './hooks/use${name}';`);
  }
  result.instructions.push(`Ensure navRoutes.generated.ts includes route: "${navRoute}"`);

  return result;
}

function navRouteToApiPath(navRoute: string): string {
  return `/api/${navRoute.replace(/\./g, '/')}`;
}

function generateApiClient(
  name: string,
  nameLower: string,
  navRoute: string,
  apiPath: string,
  methods: string[]
): string {
  const template = `/**
 * ${name} API Client
 *
 * Auto-generated by SmartStack MCP - DO NOT EDIT MANUALLY
 * NavRoute: ${navRoute}
 * API Path: ${apiPath}
 */

import { getRoute } from '../routes/navRoutes.generated';
import { apiClient } from '../lib/apiClient';
import type {
  ${name},
  ${name}CreateRequest,
  ${name}UpdateRequest,
  ${name}ListResponse,
  PaginatedRequest,
  PaginatedResponse
} from '../types/${nameLower}';

const ROUTE = getRoute('${navRoute}');

export const ${nameLower}Api = {
${methods.includes('getAll') ? `  /**
   * Get all ${name}s with pagination
   */
  async getAll(params?: PaginatedRequest): Promise<PaginatedResponse<${name}>> {
    const response = await apiClient.get<${name}ListResponse>(ROUTE.api, { params });
    return response.data;
  },
` : ''}
${methods.includes('getById') ? `  /**
   * Get ${name} by ID
   */
  async getById(id: string): Promise<${name}> {
    const response = await apiClient.get<${name}>(\`\${ROUTE.api}/\${id}\`);
    return response.data;
  },
` : ''}
${methods.includes('create') ? `  /**
   * Create new ${name}
   */
  async create(data: ${name}CreateRequest): Promise<${name}> {
    const response = await apiClient.post<${name}>(ROUTE.api, data);
    return response.data;
  },
` : ''}
${methods.includes('update') ? `  /**
   * Update existing ${name}
   */
  async update(id: string, data: ${name}UpdateRequest): Promise<${name}> {
    const response = await apiClient.put<${name}>(\`\${ROUTE.api}/\${id}\`, data);
    return response.data;
  },
` : ''}
${methods.includes('delete') ? `  /**
   * Delete ${name}
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(\`\${ROUTE.api}/\${id}\`);
  },
` : ''}
${methods.includes('search') ? `  /**
   * Search ${name}s
   */
  async search(query: string, params?: PaginatedRequest): Promise<PaginatedResponse<${name}>> {
    const response = await apiClient.get<${name}ListResponse>(\`\${ROUTE.api}/search\`, {
      params: { q: query, ...params }
    });
    return response.data;
  },
` : ''}
${methods.includes('export') ? `  /**
   * Export ${name}s to file
   */
  async export(format: 'csv' | 'xlsx' | 'pdf' = 'xlsx'): Promise<Blob> {
    const response = await apiClient.get(\`\${ROUTE.api}/export\`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  },
` : ''}
  /**
   * Get the NavRoute for this API
   */
  getRoute() {
    return ROUTE;
  },
};

export default ${nameLower}Api;
`;

  return template;
}

function generateTypes(name: string): string {
  return `/**
 * ${name} Types
 *
 * Auto-generated by SmartStack MCP - Customize as needed
 */

export interface ${name} {
  id: string;
  code: string;
  name?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface ${name}CreateRequest {
  code: string;
  name?: string;
  description?: string;
}

export interface ${name}UpdateRequest {
  code?: string;
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface ${name}ListResponse {
  items: ${name}[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface PaginatedRequest {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filter?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}
`;
}

function generateHook(name: string, nameLower: string, methods: string[]): string {
  return `/**
 * ${name} React Query Hooks
 *
 * Auto-generated by SmartStack MCP - DO NOT EDIT MANUALLY
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { ${nameLower}Api } from '../services/api/${nameLower}';
import type { ${name}, ${name}CreateRequest, ${name}UpdateRequest, PaginatedRequest, PaginatedResponse } from '../types/${nameLower}';

const QUERY_KEY = '${nameLower}s';

${methods.includes('getAll') ? `/**
 * Hook to fetch paginated ${name} list
 */
export function use${name}List(
  params?: PaginatedRequest,
  options?: Omit<UseQueryOptions<PaginatedResponse<${name}>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [QUERY_KEY, 'list', params],
    queryFn: () => ${nameLower}Api.getAll(params),
    ...options,
  });
}
` : ''}
${methods.includes('getById') ? `/**
 * Hook to fetch single ${name} by ID
 */
export function use${name}(
  id: string | undefined,
  options?: Omit<UseQueryOptions<${name}>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: () => ${nameLower}Api.getById(id!),
    enabled: !!id,
    ...options,
  });
}
` : ''}
${methods.includes('create') ? `/**
 * Hook to create new ${name}
 */
export function use${name}Create(
  options?: UseMutationOptions<${name}, Error, ${name}CreateRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ${name}CreateRequest) => ${nameLower}Api.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    ...options,
  });
}
` : ''}
${methods.includes('update') ? `/**
 * Hook to update existing ${name}
 */
export function use${name}Update(
  options?: UseMutationOptions<${name}, Error, { id: string; data: ${name}UpdateRequest }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => ${nameLower}Api.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'detail', id] });
    },
    ...options,
  });
}
` : ''}
${methods.includes('delete') ? `/**
 * Hook to delete ${name}
 */
export function use${name}Delete(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ${nameLower}Api.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    ...options,
  });
}
` : ''}
${methods.includes('search') ? `/**
 * Hook to search ${name}s
 */
export function use${name}Search(
  query: string,
  params?: PaginatedRequest,
  options?: Omit<UseQueryOptions<PaginatedResponse<${name}>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [QUERY_KEY, 'search', query, params],
    queryFn: () => ${nameLower}Api.search(query, params),
    enabled: query.length >= 2,
    ...options,
  });
}
` : ''}
`;
}

function formatResult(result: ScaffoldResult, input: ScaffoldApiClientInput): string {
  const lines: string[] = [];

  lines.push(`# Scaffold API Client: ${input.name}`);
  lines.push('');

  if (input.options?.dryRun) {
    lines.push('> **DRY RUN** - No files were written');
    lines.push('');
  }

  lines.push(`## NavRoute Integration`);
  lines.push('');
  lines.push(`- **NavRoute**: \`${input.navRoute}\``);
  lines.push(`- **API Path**: \`${navRouteToApiPath(input.navRoute)}\``);
  lines.push(`- **Methods**: ${input.methods.join(', ')}`);
  lines.push('');

  lines.push('## Generated Files');
  lines.push('');

  for (const file of result.files) {
    const relativePath = file.path.replace(/\\/g, '/').split('/src/').pop() || file.path;
    lines.push(`### ${relativePath}`);
    lines.push('');
    lines.push('```typescript');
    lines.push(file.content.substring(0, 1500) + (file.content.length > 1500 ? '\n// ... (truncated)' : ''));
    lines.push('```');
    lines.push('');
  }

  lines.push('## Next Steps');
  lines.push('');
  for (const instruction of result.instructions) {
    lines.push(`- ${instruction}`);
  }
  lines.push('');
  lines.push('## Required Setup');
  lines.push('');
  lines.push('1. Ensure `navRoutes.generated.ts` exists (run `scaffold_routes`)');
  lines.push('2. Configure `apiClient` with base URL and auth interceptors');
  lines.push('3. Install dependencies: `npm install @tanstack/react-query axios`');

  return lines.join('\n');
}
